import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { API_ENDPOINTS } from "../../config/api";
import { formatDate } from "../../utils/dateFormatting";
import { getBotIcon } from "../../utils/botFormatting";

import Button from "../../components/Button";
import { Table, Row, Column, ActionsColumn } from "../../components/Table";
import {
  LoadingState,
  EmptyState,
  ErrorState
} from "../../components/CommonStates";
import Modal, {
  ModalTitle,
  ModalDescription,
  ModalActions,
  BotList,
  BotItem,
  BotCheckbox,
  BotLabel,
  BotIcon
} from "../../components/Modal";
import { Input } from "../../components/Input";
import { Header } from "../../components/AdminTab";

const SectionDescription = styled.p`
  margin: 0;
  font-size: 1rem;
  padding-bottom: 0;
  color: #6c757d;
`;

function UsernameRulesTab({ onError, onSuccess, availableBots }) {
  const [usernameRules, setUsernameRules] = useState([]);
  const [usernameRulesLoading, setUsernameRulesLoading] = useState(false);
  const [usernameRulesError, setUsernameRulesError] = useState("");
  const [usernameRuleModal, setUsernameRuleModal] = useState(null);
  const [usernameRuleFormData, setUsernameRuleFormData] = useState({
    username: "",
    allowedBots: []
  });
  const [savingUsernameRule, setSavingUsernameRule] = useState(false);
  const [deletingUsernameRules, setDeletingUsernameRules] = useState(new Set());
  const [usernameRuleDeleteConfirmation, setUsernameRuleDeleteConfirmation] =
    useState(null);

  const fetchUsernameRules = async () => {
    setUsernameRulesLoading(true);
    setUsernameRulesError("");

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.adminUsernameRules, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      const data = await response.json();

      if (response.ok) {
        setUsernameRules(data.rules || data.usernameRules || []);
      } else {
        setUsernameRulesError(data.error || "Failed to fetch username rules");
      }
    } catch (error) {
      setUsernameRulesError("Network error. Please try again.");
    } finally {
      setUsernameRulesLoading(false);
    }
  };

  const createUsernameRule = async (ruleData) => {
    setSavingUsernameRule(true);
    setUsernameRulesError("");

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.createUsernameRuleWithBots, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          username: ruleData.username,
          allowedBots: ruleData.allowedBots
        })
      });

      const data = await response.json();

      if (response.ok) {
        await fetchUsernameRules();
        onSuccess(data.message || "Username rule created successfully");
        setUsernameRuleModal(null);
        setUsernameRuleFormData({ username: "", allowedBots: [] });
      } else {
        setUsernameRulesError(data.error || "Failed to create username rule");
      }
    } catch (error) {
      setUsernameRulesError("Network error. Please try again.");
    } finally {
      setSavingUsernameRule(false);
    }
  };

  const updateUsernameRule = async (ruleId, ruleData) => {
    setSavingUsernameRule(true);
    setUsernameRulesError("");

    try {
      const token = localStorage.getItem("token");

      const currentRule = usernameRules.find((r) => r.id === ruleId);
      if (currentRule && currentRule.username !== ruleData.username) {
        const response = await fetch(API_ENDPOINTS.updateUsernameRule(ruleId), {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ username: ruleData.username })
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || "Failed to update username rule");
        }
      }

      const botsResponse = await fetch(
        API_ENDPOINTS.replaceUsernameRuleBots(ruleId),
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ botIds: ruleData.allowedBots })
        }
      );

      if (!botsResponse.ok) {
        const data = await botsResponse.json();
        throw new Error(data.error || "Failed to update username rule bots");
      }

      await fetchUsernameRules();
      onSuccess("Username rule updated successfully");
      setUsernameRuleModal(null);
      setUsernameRuleFormData({ username: "", allowedBots: [] });
    } catch (error) {
      setUsernameRulesError(
        error.message || "Network error. Please try again."
      );
    } finally {
      setSavingUsernameRule(false);
    }
  };

  const deleteUsernameRule = async (ruleId) => {
    setDeletingUsernameRules((prev) => new Set(prev).add(ruleId));
    setUsernameRulesError("");

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.deleteUsernameRule(ruleId), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      const data = await response.json();

      if (response.ok) {
        setUsernameRules((prev) => prev.filter((rule) => rule.id !== ruleId));
        onSuccess(data.message || "Username rule deleted successfully");
      } else {
        setUsernameRulesError(data.error || "Failed to delete username rule");
      }
    } catch (error) {
      setUsernameRulesError("Network error. Please try again.");
    } finally {
      setDeletingUsernameRules((prev) => {
        const newSet = new Set(prev);
        newSet.delete(ruleId);
        return newSet;
      });
      setUsernameRuleDeleteConfirmation(null);
    }
  };

  const handleCreateUsernameRule = () => {
    setUsernameRuleModal("create");
    setUsernameRuleFormData({ username: "", allowedBots: [] });
    setUsernameRulesError("");
  };

  const handleEditUsernameRule = (ruleObj) => {
    setUsernameRuleModal(ruleObj);
    setUsernameRuleFormData({
      username: ruleObj.username,
      allowedBots: ruleObj.allowedBots || []
    });
    setUsernameRulesError("");
  };

  const handleSaveUsernameRule = () => {
    if (!usernameRuleFormData.username.trim()) {
      setUsernameRulesError("Username is required");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(usernameRuleFormData.username.trim())) {
      setUsernameRulesError("Please enter a valid email address");
      return;
    }

    if (usernameRuleModal === "create") {
      createUsernameRule(usernameRuleFormData);
    } else {
      updateUsernameRule(usernameRuleModal.id, usernameRuleFormData);
    }
  };

  const handleCancelUsernameRuleModal = () => {
    setUsernameRuleModal(null);
    setUsernameRuleFormData({ username: "", allowedBots: [] });
    setUsernameRulesError("");
  };

  const handleUsernameRuleDeleteClick = (rule) => {
    setUsernameRuleDeleteConfirmation(rule);
  };

  const handleUsernameRuleDeleteConfirm = () => {
    if (usernameRuleDeleteConfirmation) {
      deleteUsernameRule(usernameRuleDeleteConfirmation.id);
    }
  };

  const handleUsernameRuleDeleteCancel = () => {
    setUsernameRuleDeleteConfirmation(null);
  };

  const handleUsernameRuleBotToggle = (botId) => {
    setUsernameRuleFormData((prev) => {
      const newAllowedBots = prev.allowedBots.includes(botId)
        ? prev.allowedBots.filter((id) => id !== botId)
        : [...prev.allowedBots, botId];

      return {
        ...prev,
        allowedBots: newAllowedBots
      };
    });
  };

  useEffect(() => {
    fetchUsernameRules();
  }, []);

  return (
    <>
      <Header>
        <SectionDescription style={{ paddingRight: "4rem" }}>
          These can be used to control access for users who haven't signed up
          yet, but we know their email address. Adding a rule here disables
          normal user-level control.
        </SectionDescription>
        <Button onClick={handleCreateUsernameRule} style={{ flexShrink: 0 }}>
          Create new rule
        </Button>
      </Header>

      {usernameRulesError && <ErrorState>{usernameRulesError}</ErrorState>}

      {usernameRulesLoading && !usernameRules.length ? (
        <LoadingState>Loading username rules...</LoadingState>
      ) : usernameRules.length === 0 ? (
        <EmptyState>
          <h4>No username rules found</h4>
          <p>Create username rules to manage bot access for specific users.</p>
        </EmptyState>
      ) : (
        <Table columns="3fr 1fr 2fr 2fr">
          <Row>
            <Column>Username</Column>
            <Column>Allowed bots</Column>
            <Column>Created</Column>
            <ActionsColumn>Actions</ActionsColumn>
          </Row>

          {usernameRules
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .map((rule) => (
              <Row key={rule.id}>
                <Column data-label="Username">{rule.username}</Column>
                <Column data-label="Allowed Bots">
                  {rule.allowedBots && rule.allowedBots.length > 0 ? (
                    <span style={{ fontWeight: "500" }}>
                      {rule.allowedBots.length} of {availableBots.length}
                    </span>
                  ) : (
                    <span style={{ color: "#6c757d", fontStyle: "italic" }}>
                      0 of {availableBots.length}
                    </span>
                  )}
                </Column>
                <Column data-label="Created">
                  {formatDate(rule.createdAt)}
                </Column>
                <ActionsColumn data-label="Actions">
                  <Button
                    onClick={() => handleEditUsernameRule(rule)}
                    disabled={deletingUsernameRules.has(rule.id)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => handleUsernameRuleDeleteClick(rule)}
                    disabled={deletingUsernameRules.has(rule.id)}
                  >
                    {deletingUsernameRules.has(rule.id)
                      ? "Deleting..."
                      : "Delete"}
                  </Button>
                </ActionsColumn>
              </Row>
            ))}
        </Table>
      )}

      {/* Username Rule Management Modal */}
      <Modal
        isOpen={!!usernameRuleModal}
        onClose={handleCancelUsernameRuleModal}
      >
        <ModalTitle>
          {usernameRuleModal === "create"
            ? "Create username rule"
            : "Edit username rule"}
        </ModalTitle>
        <ModalDescription>
          {usernameRuleModal === "create"
            ? "Create a new username rule to determine which bots are available to this specific user"
            : `Determine which bots are available to this username`}
        </ModalDescription>

        {usernameRulesError && <ErrorState>{usernameRulesError}</ErrorState>}

        <div style={{ marginBottom: "1.5rem" }}>
          <label
            style={{
              display: "block",
              marginBottom: "0.5rem",
              fontWeight: "600"
            }}
          >
            Username
          </label>
          <Input
            type="email"
            value={usernameRuleFormData.username}
            onChange={(e) =>
              setUsernameRuleFormData((prev) => ({
                ...prev,
                username: e.target.value
              }))
            }
            placeholder="user@example.com"
            disabled={savingUsernameRule}
          />
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <label
            style={{
              display: "block",
              marginBottom: "0.5rem",
              fontWeight: "600"
            }}
          >
            Allowed bots
          </label>
          <BotList>
            {availableBots.map((bot) => (
              <BotItem
                key={bot.id}
                onClick={() => handleUsernameRuleBotToggle(bot.id)}
              >
                <BotCheckbox
                  type="checkbox"
                  checked={usernameRuleFormData.allowedBots.includes(bot.id)}
                  onChange={() => {}}
                  disabled={savingUsernameRule}
                />
                <BotLabel>
                  <BotIcon>{getBotIcon(bot)}</BotIcon>
                  {bot.name}
                </BotLabel>
              </BotItem>
            ))}
          </BotList>
        </div>

        <ModalActions>
          <Button
            variant="secondary"
            onClick={handleCancelUsernameRuleModal}
            disabled={savingUsernameRule}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveUsernameRule}
            disabled={savingUsernameRule}
          >
            {savingUsernameRule ? "Saving..." : "Save"}
          </Button>
        </ModalActions>
      </Modal>

      {/* Username Rule Delete Confirmation Modal */}
      <Modal
        isOpen={!!usernameRuleDeleteConfirmation}
        onClose={handleUsernameRuleDeleteCancel}
      >
        <ModalTitle>
          Delete rule for {usernameRuleDeleteConfirmation?.username}?
        </ModalTitle>
        <ModalDescription>
          Are you sure you want to permanently delete this user rule?
        </ModalDescription>
        <ModalActions>
          <Button variant="secondary" onClick={handleUsernameRuleDeleteCancel}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleUsernameRuleDeleteConfirm}
            disabled={deletingUsernameRules.has(
              usernameRuleDeleteConfirmation?.id
            )}
          >
            {deletingUsernameRules.has(usernameRuleDeleteConfirmation?.id)
              ? "Deleting..."
              : "Delete"}
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
}

export default UsernameRulesTab;
